import Alpine from 'alpinejs';
import swipePlugin from "alpinejs-swipe";
import intersect from '@alpinejs/intersect';
import collapse from '@alpinejs/collapse';
import gsapfunctions from "./composed/gsapfunctions.js";
import headerScroll from './components/headerscroll.js';
import darkModeToggle from './components/darkModeToggle.js';
import animationsComponent from "./components/animationsComponent.js";
import anchorScroll from './components/anchorScroll.js';
import filtersComponent from './components/filtersComponent.js';

// Alpine setup
window.Alpine = Alpine;
Alpine.plugin(swipePlugin);
Alpine.plugin(collapse);
Alpine.plugin(intersect);

// Performance-optimized component registry
const componentRegistry = new Map([
    ['headerScroll', { component: headerScroll, loaded: false, priority: 'high' }],
    ['darkModeToggle', { component: darkModeToggle, loaded: false, priority: 'high' }],
    ['animationsComponent', { component: animationsComponent, loaded: false, priority: 'low' }],
    ['anchorScroll', { component: anchorScroll, loaded: false, priority: 'medium' }],
    ['filtersComponent', { component: filtersComponent, loaded: false, priority: 'low' }]
]);

// GSAP instance management
let gsapInstance = gsapfunctions;
let gsapInitialized = false;

// Optimized component registration with priority handling
function registerComponentIfNeeded(name) {
    const entry = componentRegistry.get(name);
    if (entry && !entry.loaded) {
        Alpine.data(name, entry.component);
        entry.loaded = true;
        return true;
    }
    return false;
}

// Synchronous, low-overhead component detection and registration
function detectAndRegisterRequiredComponents() {
    // Early return if all components loaded
    if ([...componentRegistry.values()].every(entry => entry.loaded)) {
        return new Set();
    }

    // Keep selectors simple and fast (we want this safe to run before Alpine.start)
    const componentSelectors = new Map([
        ['headerScroll', '[x-data*="headerScroll"]'],
        ['darkModeToggle', '[x-data*="darkModeToggle"]'],
        ['animationsComponent', '[x-data*="animationsComponent"]'],
        ['anchorScroll', '[x-data*="anchorScroll"], [data-anchor-scroll], a[href^="#"]'],
        ['filtersComponent', '[x-data*="filtersComponent"], [data-filter], .filter-container']
    ]);

    const foundComponents = new Set();

    componentSelectors.forEach((selector, name) => {
        if (document.querySelector(selector)) {
            foundComponents.add(name);
        }
    });

    // Register by priority so critical behaviours exist at parse time
    const priorities = ['high', 'medium', 'low'];
    priorities.forEach(priority => {
        foundComponents.forEach(name => {
            const entry = componentRegistry.get(name);
            if (entry && entry.priority === priority) {
                registerComponentIfNeeded(name);
            }
        });
    });

    return foundComponents;
}

// Smart GSAP detection - comprehensive element checking
function detectGSAPRequirements() {
    // Cache the result to avoid repeated DOM queries
    if (detectGSAPRequirements._cached !== undefined) {
        return detectGSAPRequirements._cached;
    }
    
    const gsapSelectors = [
        '[x-data*="animationsComponent"]',
        '[data-gsap]',
        '.gsap-animation',
        '[data-aos]',
        '.animate-on-scroll',
        '.parallax-img',
        '.swipe-container',
        '.project-image', 
        '.pinned',
        '.parallax-content',
        '.TextReveal',
        '.award-table',
        '.side-drawer',
        '.burger',
        '.image-trail',
        '.swish-reveal',
        '.navbar-side-item'
    ];
    
    const result = gsapSelectors.some(selector => document.querySelector(selector));
    detectGSAPRequirements._cached = result;
    return result;
}

// Optimized GSAP initialization with performance considerations
function initializeGSAP() {
    if (gsapInitialized || !detectGSAPRequirements()) {
        return;
    }
    
    try {
        // Cleanup previous instance if exists
        if (gsapInstance && typeof gsapInstance.destroy === 'function') {
            gsapInstance.destroy();
        }
        
        // Initialize GSAP in next frame to avoid blocking
        requestAnimationFrame(() => {
            if (gsapInstance && typeof gsapInstance.init === 'function') {
                gsapInstance.init();
                gsapInitialized = true;
            }
        });
        
    } catch (error) {
        console.warn('GSAP initialization failed:', error);
    }
}

// Optimized cache busting with minimal DOM operations
function optimizeCacheBusting() {
    const elementsToUpdate = document.querySelectorAll('[x-load-src]:not([data-cache-busted])');
    if (elementsToUpdate.length === 0) return;
    
    const bustValue = Date.now();
    
    // Batch DOM updates for better performance
    elementsToUpdate.forEach(el => {
        const src = el.getAttribute('x-load-src');
        if (src && !src.includes('?v=')) {
            const separator = src.includes('?') ? '&' : '?';
            el.setAttribute('x-load-src', `${src}${separator}v=${bustValue}`);
            el.setAttribute('data-cache-busted', 'true');
        }
    });
}



// Performance-optimized main initialization
function optimizedInit() {
    try {
        // Step 1: Optimize cache busting (minimal DOM impact)
        optimizeCacheBusting();

        // Step 2: Detect and register any late-needed components
        const foundComponents = detectAndRegisterRequiredComponents();

        // NOTE: Do NOT call Alpine.initTree(document.body) here.
        // Alpine.start() already initialises the tree once, and re-walking can be expensive
        // and can duplicate listeners.

        // Step 3: Initialize GSAP only if needed (deferred)
        if (foundComponents.has('animationsComponent') || detectGSAPRequirements()) {
            // Let rendering settle first
            setTimeout(initializeGSAP, 120);
        }

    } catch (error) {
        console.error('Initialization error:', error);
    }
}

// Intersection Observer for progressive enhancement
function setupProgressiveLoading() {
    if (!('IntersectionObserver' in window)) return;
    
    const componentObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const element = entry.target;
                const xDataValue = element.getAttribute('x-data');
                
                if (xDataValue) {
                    // Check if any unloaded components match this element
                    componentRegistry.forEach((config, name) => {
                        if (!config.loaded && xDataValue.includes(name)) {
                            registerComponentIfNeeded(name);
                        }
                    });
                }
                
                // Stop observing this element
                componentObserver.unobserve(element);
            }
        });
    }, {
        rootMargin: '100px 0px', // Load 100px before entering viewport
        threshold: 0.1
    });
    
    const schedule = (cb) => {
        if ('requestIdleCallback' in window) {
            requestIdleCallback(cb, { timeout: 3000 });
        } else {
            setTimeout(cb, 250);
        }
    };

    schedule(() => {
        const elements = document.querySelectorAll('[x-data]');
        elements.forEach(el => componentObserver.observe(el));
    });
}

// Sanitize malformed Alpine expressions to prevent runtime parse errors (robust version)
function sanitizeAlpineAttributes(root = document) {
    // Quick bail-out: if there are no common Alpine directives, skip the full scan
    if (!root.querySelector('[x-data], [x-init], [x-cloak], [x-show], [x-text], [x-model], [x-for], [x-if], [x-bind]')) {
        return;
    }

    // Boolean directives that are valid without a value
    const booleanDirectives = new Set(['x-cloak']);

    // Iterate all elements once and filter attributes in JS (no fragile CSS selectors)
    const nodes = root.querySelectorAll('*');

    const isBroken = (val, name) => {
        if (booleanDirectives.has(name)) return false; // allowed to be empty
        if (val == null) return true;
        const v = String(val).trim();
        if (!v) return true; // empty string
        // Heuristics seen in prod: stray semicolons or "; = () =>" fragments or placeholders
        if (/^;+$/m.test(v)) return true;
        if (/^[;=()\s]*$/.test(v)) return true;
        if (/^;\s*=\s*\(\)\s*=>/.test(v)) return true;
        if (v === 'undefined' || v === 'null') return true;
        return false;
    };

    nodes.forEach((el) => {
        if (!el.hasAttributes()) return;
        // Copy to array to avoid live mutation issues
        Array.from(el.attributes).forEach((attr) => {
            const name = attr.name;
            if (!name.startsWith('x-')) return;

            const value = attr.value;
            if (isBroken(value, name)) {
                // Non-destructive fixes by directive type
                if (name === 'x-data') {
                    // Replace with a harmless empty component object
                    el.setAttribute('x-data', '{}');
                    return;
                }
                if (name === 'x-init' || name.startsWith('x-on:') || name === 'x-bind' || name.startsWith('x-bind:')) {
                    // Optional behaviours: remove if broken
                    el.removeAttribute(name);
                    return;
                }
                // As a last resort, neutralise the directive
                el.setAttribute(name, '');
            }
        });
    });
}

// Enhanced app initialization with optimal timing
function initializeApp() {
    // Critical: Start Alpine immediately for best perceived performance
    // Clean any malformed x-* attributes to avoid Alpine parse errors
    sanitizeAlpineAttributes(document);

    // Register required components BEFORE Alpine parses x-data
    // This prevents "component not defined" errors and avoids needing expensive re-inits.
    detectAndRegisterRequiredComponents();

    try {
        Alpine.start();
    } catch (e) {
        console.warn('Alpine.start() failed on first pass, attempting sanitation + retry', e);
        sanitizeAlpineAttributes(document);
        detectAndRegisterRequiredComponents();
        Alpine.start();
    }
    
    // Non-critical initialization using optimal scheduling
    if ('requestIdleCallback' in window) {
        requestIdleCallback(optimizedInit, { timeout: 2000 });
    } else {
        // Fallback with minimal delay
        setTimeout(optimizedInit, 50);
    }
    
    // Setup progressive loading enhancement
    setupProgressiveLoading();
}

// Optimized event handling with performance considerations
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApp, { 
        passive: true, 
        once: true 
    });
} else {
    // DOM already loaded - initialize immediately
    initializeApp();
}

// Preload critical components during browser idle time
if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
        // Register high-priority components that are commonly used
        const criticalComponents = ['darkModeToggle', 'headerScroll'];
        criticalComponents.forEach(name => {
            const entry = componentRegistry.get(name);
            if (entry && entry.priority === 'high' && !entry.loaded) {
                // Only register if we can detect the component in DOM
                const selectors = {
                    darkModeToggle: '[x-data*="darkModeToggle"]',
                    headerScroll: '[x-data*="headerScroll"]'
                };
                if (document.querySelector(selectors[name])) {
                    registerComponentIfNeeded(name);
                }
            }
        });
    }, { timeout: 5000 });
}

// Performance monitoring utilities for development
const createPerformanceUtils = () => ({
    getLoadedComponents: () => {
        const loaded = [];
        componentRegistry.forEach((entry, name) => {
            if (entry.loaded) loaded.push(name);
        });
        return loaded;
    },
    getGSAPStatus: () => ({
        initialized: gsapInitialized,
        required: detectGSAPRequirements(),
        instance: !!gsapInstance
    }),
    forceGSAPInit: () => {
        detectGSAPRequirements._cached = true;
        initializeGSAP();
    },
    componentStats: () => {
        const stats = { total: 0, loaded: 0, byPriority: {} };
        componentRegistry.forEach((entry, name) => {
            stats.total++;
            if (entry.loaded) stats.loaded++;
            if (!stats.byPriority[entry.priority]) stats.byPriority[entry.priority] = { total: 0, loaded: 0 };
            stats.byPriority[entry.priority].total++;
            if (entry.loaded) stats.byPriority[entry.priority].loaded++;
        });
        return stats;
    }
});



// Expose utilities globally
window.AlpineUtils = {
    registerComponentIfNeeded,
    componentRegistry,
    initializeGSAP,
    performance: createPerformanceUtils()
};